import { useEffect, useRef } from 'react';
import { tg, isTelegram } from '@/lib/telegram';

export interface MainButtonProps {
  text: string;
  /** Правая часть подписи моноширинным — обычно сумма */
  suffix?: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  hint?: string;
}

/**
 * Одна кнопка действия на экран.
 *
 * Внутри Telegram управляет нативной MainButton: она всегда над клавиатурой
 * и не перекрывается. В обычном браузере рисует свою полосу внизу — иначе
 * витрину нельзя было бы разрабатывать и тестировать без телефона.
 */
export function MainButton({ text, suffix, onClick, disabled, loading, hint }: MainButtonProps) {
  const handler = useRef(onClick);
  handler.current = onClick;

  const label = suffix ? `${text} · ${suffix}` : text;

  useEffect(() => {
    if (!isTelegram || !tg) return;
    const mb = tg.MainButton;
    const cb = () => handler.current?.();

    mb.setParams({
      text: label.toUpperCase(),
      color: '#CD7432',
      text_color: '#FFFFFF',
      is_active: !disabled && !loading,
      is_visible: true,
    });
    if (loading) mb.showProgress(true);
    else mb.hideProgress();

    mb.onClick(cb);
    return () => {
      mb.offClick(cb);
      mb.hide();
    };
  }, [label, disabled, loading]);

  if (isTelegram) return null;

  return (
    <div className="mainbtn-slot">
      <button
        type="button"
        className="mainbtn"
        disabled={disabled || loading}
        onClick={() => handler.current?.()}
      >
        {loading ? 'Подождите…' : text}
        {suffix && !loading && <span className="mainbtn__sum">·&nbsp;{suffix}</span>}
      </button>
      {hint && <span className="mainbtn-slot__hint">{hint}</span>}
    </div>
  );
}

/**
 * Нативная кнопка «назад» Telegram.
 * Собственную стрелку в шапке не рисуем: два способа вернуться на одном
 * экране — источник ошибок навигации.
 */
export function useBackButton(onBack: () => void, visible: boolean) {
  const handler = useRef(onBack);
  handler.current = onBack;

  useEffect(() => {
    if (!isTelegram || !tg) return;
    const bb = tg.BackButton;
    const cb = () => handler.current();
    if (visible) {
      bb.onClick(cb);
      bb.show();
    } else {
      bb.hide();
    }
    return () => {
      bb.offClick(cb);
    };
  }, [visible]);
}
